import {
  createStyles,
  Group,
  LoadingOverlay,
  SegmentedControl,
  Stack,
  StackProps,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { isEmpty } from 'lodash/fp';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { LegendLabel } from './LegendLabel';
import {
  ScatterChartWidgetEventsConfig,
  WidgetDataRangeEnum,
} from './scatter-chart.types';
import {
  getXAxisTicksForLast24Hours,
  getXAxisTicksForLast30Days,
  getXAxisTicksForLast7Days,
} from './scatter-chart.utils';
import { TooltipContent } from './TooltipContent';
import { XAxisLabel, XAxisLabelProps } from './XAxisLabel';
import { FORMATTERS } from '../../../utils/formatters';
import { OfflineDeviceStateTooltip } from '../../common/OfflineDeviceStateTooltip';
import { NumberFormatType, ScaleType } from '../../widgets.types';

export interface ScatterChartWidgetProps {
  id?: string;
  title: string;

  eventsConfig: ScatterChartWidgetEventsConfig;

  data: Array<{
    eventId: string;
    timestamp: number;
    value: number | null;
  }>;

  stackProps?: StackProps;
  range?: WidgetDataRangeEnum;
  setRange?: Dispatch<SetStateAction<WidgetDataRangeEnum>>;
  isLoading?: boolean;
  scaleType: ScaleType;
  numberFormat: NumberFormatType;
  numOfDecimals: number;

  isDeviceOffline?: boolean;
  lastUpdateTimestamp?: string;
}

export function ScatterChartWidget({
  id,
  title,
  eventsConfig,
  data,
  stackProps,
  range,
  setRange,
  isLoading = false,
  scaleType,
  numberFormat,
  numOfDecimals,

  isDeviceOffline,
  lastUpdateTimestamp,
}: ScatterChartWidgetProps) {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const [disabledEventIds, setDisabledEventIds] = useState<string[]>([]);

  const formatter = useCallback(
    (value: number | undefined) =>
      FORMATTERS[numberFormat](value, numOfDecimals),
    [numberFormat, numOfDecimals]
  );

  const nullifyNegativeValues = useCallback((rawData: typeof data) => {
    return rawData.map((dataItem) => {
      if (dataItem.value !== null && dataItem.value < 0) {
        return { ...dataItem, value: null };
      }

      return dataItem;
    });
  }, []);

  const adjustedData = useMemo(() => {
    let allDataPoints;

    if (scaleType === 'log') {
      allDataPoints = nullifyNegativeValues(data);
    } else {
      allDataPoints = data;
    }

    const scatterDataMap = Object.keys(eventsConfig).reduce((acc, eventId) => {
      acc[eventId] = [];
      return acc;
    }, {} as Record<string, typeof data>);

    allDataPoints.forEach((dataPoint) => {
      scatterDataMap[dataPoint.eventId].push(dataPoint);
    });

    return scatterDataMap;
  }, [data, eventsConfig, nullifyNegativeValues, scaleType]);

  const onLegendItemClick = useCallback((eventIdToToggle: string) => {
    setDisabledEventIds((prev) => {
      if (prev.includes(eventIdToToggle)) {
        return prev.filter((eventId) => eventId !== eventIdToToggle);
      } else {
        return prev.concat(eventIdToToggle);
      }
    });
  }, []);

  const getTicks = useCallback(() => {
    if (range === WidgetDataRangeEnum.Week) {
      return getXAxisTicksForLast7Days();
    }

    if (range === WidgetDataRangeEnum.Month) {
      return getXAxisTicksForLast30Days();
    }

    return getXAxisTicksForLast24Hours();
  }, [range]);

  const getInterval = useCallback(() => {
    if (range === WidgetDataRangeEnum.Week) {
      return 0;
    }

    if (range === WidgetDataRangeEnum.Month) {
      return 3;
    }

    return 3;
  }, [range]);

  return (
    <Stack
      className={classes.container}
      p="xl"
      h="100%"
      w="100%"
      bg="white"
      justify="center"
      spacing="xl"
      pos="relative"
      {...stackProps}
    >
      <LoadingOverlay visible={isLoading} />

      <Group align="center" position="apart">
        <Group noWrap align="center" spacing="sm">
          <Text
            size="md"
            data-testid="dashboard-scatter-chart-widget-name"
            color="gray.7"
            truncate
          >
            {title}
          </Text>

          {isDeviceOffline ? (
            <OfflineDeviceStateTooltip
              lastUpdateTimestamp={lastUpdateTimestamp}
            />
          ) : null}
        </Group>

        {range && id !== 'preview' ? (
          <SegmentedControl
            size="xs"
            data={[
              { value: WidgetDataRangeEnum.Month, label: 'Last 30 Days' },
              { value: WidgetDataRangeEnum.Week, label: 'Last 7 Days' },
              { value: WidgetDataRangeEnum.Day, label: 'Last 24hr' },
            ]}
            value={range}
            onChange={(range: WidgetDataRangeEnum) => setRange?.(range)}
          />
        ) : null}
      </Group>

      {isEmpty(data) ? (
        <Stack align="center" justify="center" w="100%" h="100%">
          <Text size="sm" color="gray.5">
            No data
          </Text>
        </Stack>
      ) : (
        <ResponsiveContainer
          width="100%"
          minHeight={100}
          className={classes.chartWrapper}
        >
          <ScatterChart
            margin={{
              top: 10,
              right: 25,
              bottom: 10,
              left: 0,
            }}
          >
            <CartesianGrid
              stroke={theme.colors.gray[2]}
              strokeDasharray="3 3"
            />

            <YAxis
              dataKey="value"
              width={60}
              tickFormatter={(value) => formatter(value) || 'N/A'}
              type="number"
              scale={scaleType}
              domain={['auto', 'auto']}
              tickLine={{
                strokeWidth: 1,
                stroke: theme.colors.gray[2],
              }}
              tick={{
                fill: theme.colors.gray[4],
                fontSize: theme.fontSizes.xs,
              }}
              axisLine={{
                strokeWidth: 1,
                stroke: theme.colors.gray[2],
              }}
            />

            <XAxis
              dataKey="timestamp"
              tickMargin={5}
              tickSize={5}
              domain={['auto', 'auto']}
              type="number"
              scale="time"
              interval={getInterval()}
              ticks={getTicks()}
              tickLine={{
                strokeWidth: 1,
                stroke: theme.colors.gray[2],
              }}
              tick={(props: XAxisLabelProps) => (
                <XAxisLabel {...props} range={range} />
              )}
              axisLine={{
                strokeWidth: 1,
                stroke: theme.colors.gray[2],
              }}
            />

            <Tooltip<number, string>
              isAnimationActive={false}
              content={(props: TooltipProps<number, string>) => (
                <TooltipContent
                  {...props}
                  eventsConfig={eventsConfig}
                  customFormatter={formatter}
                />
              )}
            />

            {Object.values(eventsConfig).map((eventConfig) => {
              if (disabledEventIds.includes(eventConfig.id)) return null;

              return (
                <Scatter
                  key={eventConfig.id}
                  name={eventConfig.event_display_name}
                  data={adjustedData[eventConfig.id]}
                  fill={theme.fn.themeColor(eventConfig.color)}
                />
              );
            })}

            <Legend
              align="left"
              wrapperStyle={{ bottom: 0 }}
              content={() => (
                <Group>
                  {Object.values(eventsConfig).map((eventConfig) => (
                    <LegendLabel
                      key={eventConfig.id}
                      label={eventConfig.event_display_name}
                      color={eventConfig.color}
                      disabled={disabledEventIds.includes(eventConfig.id)}
                      onClick={() => onLegendItemClick(eventConfig.id)}
                    />
                  ))}
                </Group>
              )}
            />
          </ScatterChart>
        </ResponsiveContainer>
      )}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    borderRadius: theme.radius.lg,
  },
  indicator: {
    width: 14,
    height: 14,
    borderRadius: theme.radius.sm,
  },
  chartWrapper: {
    svg: {
      overflow: 'visible',
    },
  },
}));
